import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  BILLING_TIERS,
  TIER_SCENARIOS,
  BILLING_SCENARIO_LABELS,
  type BillingScenario,
} from '@/lib/billing-scenarios';

/**
 * Props for the ScenarioCoverage component.
 */
interface ScenarioCoverageProps {
  /** The scenarios that have at least one configured price for the product. */
  configuredScenarios: BillingScenario[];
  /** Optional additional class names. */
  className?: string;
}

/**
 * Shows, per eligibility tier, which billing scenarios the product has configured and which are
 * missing, and warns when a tier has no configured plan (so admins don't reproduce a silent
 * "no plans" experience for those organizations).
 */
export function ScenarioCoverage({ configuredScenarios, className }: ScenarioCoverageProps) {
  const configured = new Set(configuredScenarios);

  // Tiers with no configured scenario at all - organizations there would see no plans.
  const uncoveredTiers = BILLING_TIERS.filter(
    (tier) => !TIER_SCENARIOS[tier].some((scenario) => configured.has(scenario))
  );

  return (
    <div className={cn('space-y-3', className)}>
      <div>
        <h4 className="text-sm font-medium">Scenario coverage</h4>
        <p className="text-sm text-muted-foreground">
          Which billing scenarios this product offers each eligibility tier.
        </p>
      </div>

      {uncoveredTiers.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Some tiers have no plan configured</AlertTitle>
          <AlertDescription>
            Organizations in the {uncoveredTiers.join(', ')} tier
            {uncoveredTiers.length > 1 ? 's' : ''} will see no purchasable plans for this product
            until a scenario is configured for them.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        {BILLING_TIERS.map((tier) => (
          <div key={tier} className="rounded-md border p-3">
            <div className="mb-2 text-sm font-medium">{tier}</div>
            <div className="flex flex-col gap-1">
              {TIER_SCENARIOS[tier].map((scenario) => {
                const isConfigured = configured.has(scenario);
                return (
                  <div key={scenario} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{BILLING_SCENARIO_LABELS[scenario]}</span>
                    {isConfigured ? (
                      <Badge variant="secondary" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Configured
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        <XCircle className="h-3 w-3" />
                        Not configured
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
